import React, { useEffect } from 'react';

import * as echarts from 'echarts';
import { Typography } from '@mui/material';

import { Container, GraphWrapper } from './styles';
import { useGetAgeGraphQuery } from 'store/api/pkd/endpoints/graphs';
import { useGetPersonAndAttributes } from 'hooks/useGetPersonAndAttributes';
import { getPersonDateOfBirth } from 'pages/MedicalInformation/utils';
import { getDateDifference } from 'utils/date';
import { isBetweenRange } from 'utils/validation';
import { getResizedChartWidth } from 'utils/charts';
import { CircleSkeleton } from 'components/SkeletonLoaders';

const AgeGraph = ({
  id,
  label,
  width,
  height,
  legendOptions,
  center = ['35%', '50%'],
}) => {
  const { person, isPersonLoading } = useGetPersonAndAttributes();
  const attribute = getPersonDateOfBirth(person?.attributes, {
    date_of_birth : '',
  });
  const dateOfBirthJoin = `${attribute?.yearOfBirth}-${attribute?.monthOfBirth}-${attribute?.dayOfBirth}`;
  const personsAge = getDateDifference(new Date(), dateOfBirthJoin, 'years');
  
  const { data: ageGraph, isLoading, isFetching } = useGetAgeGraphQuery();

  const graphData = ageGraph?.map((age) => {
    let pieColor = '#BABABA';
    const ageSplit = age.ageCategory.split('-');
    let min = 0;
    let max = 0;
    if (ageSplit.length === 1 && ageSplit[0].includes('< 20')) {
      min = 0;
      max = 19;
    } else if (ageSplit.length === 1 && ageSplit[0].includes('> 79')) {
      min = 80;
      max = 1000;
    } else {
      min = +ageSplit[0];
      max = +ageSplit[1];
    }
    if (isBetweenRange(min, max, personsAge)) {
      pieColor = '#00778B';
    }

    return {
      value     : age.count,
      name      : age.ageCategory,
      itemStyle : {
        color : pieColor,
      },
    };
  });

  useEffect(() => {
    const chartDom = document.getElementById(id);
    const myChart = echarts.init(chartDom, null, {
      width,
      height,
    });
    var option;
    option = {
      tooltip : {
        trigger   : 'item',
        formatter : '{a} <br/>{b} : {c} ({d}%)',
      },
      legend : {
        show  : true,
        icon  : 'circle',
        width : '80%',
        ...legendOptions,
      },
      series : [
        {
          name   : 'Age',
          type   : 'pie',
          radius : '50%',
          center : center,
          data   : graphData,
          label  : {
            show : false,
          },
          itemStyle : {
            borderRadius : 0,
            borderColor  : '#fff',
            borderWidth  : 2,
          },
          emphasis : {
            itemStyle : {
              shadowBlur    : 10,
              shadowOffsetX : 0,
              shadowColor   : 'rgba(0, 0, 0, 0.5)',
            },
          },
        },
      ],
    };

    myChart.setOption(option);
    const breakpointWidth = 899;
    window.addEventListener('resize', () => {
      const newWidth = getResizedChartWidth(width, breakpointWidth);
      myChart.resize({
        width : newWidth,
      });
    });
    const chartWidth = getResizedChartWidth(width, breakpointWidth);
    myChart.resize({
      width : chartWidth,
    });

    // eslint-disable-next-line
  }, [graphData]);

  return (
    <>
      <Typography
        data-testid='graph-label' fontFamily='Montserrat' fontWeight='700' variant='h6'>
        {label}
      </Typography>
      {(isLoading || isFetching || isPersonLoading) && (
        <CircleSkeleton
          height='400px' width='250px'
        />
      )}
      <GraphWrapper
        isVisible={!isLoading && !isPersonLoading}>
        <Container>
          <div
            id={id}
          />
        </Container>
      </GraphWrapper>
    </>
  );
};

export default AgeGraph;
